import type { FC } from "react";
import { useCallback, useState } from "react";

import { translate } from "app/filters/i18nFilter";
import { deleteMenuItem, renameMenuItem } from "app/pages/sales-intelligence/common-components/options-menu/items";
import type { MenuItemOption } from "app/pages/sales-intelligence/common-components/options-menu/types";
import { STATIC_LIST_PAGE_ROUTE } from "app/pages/sales-intelligence/constants/routes";
import useStaticListTrackingService from "app/pages/sales-intelligence/hooks/useStaticListTrackingService";
import useTargetAccountsTrackingService from "app/pages/sales-intelligence/hooks/useTargetAccountsTrackingService";
import EmailAlertButton from "app/pages/sales-intelligence/modules/lists/components/EmailAlertButton/EmailAlertButton";
import { ListHookOption } from "app/pages/sales-intelligence/modules/lists/components/ListHookOption/ListHookOption";
import ListItem from "app/pages/sales-intelligence/modules/lists/components/ListItem/ListItem";
import { StyledOptionsMenu } from "app/pages/sales-intelligence/modules/lists/components/styles";
import { PAGE_SIZE } from "app/pages/sales-intelligence/modules/lists/constants/constants";
import type { StaticListDto } from "app/pages/sales-intelligence/modules/lists/types/dto";
import type { IconConfig, ShortOpportunityList } from "app/pages/sales-intelligence/modules/lists/types/types";
import { getIconConfigByType } from "app/pages/sales-intelligence/modules/lists/utils";
import ListDeleteModalContainer from "app/pages/sales-intelligence/pages/lists/containers/ListDeleteModal/ListDeleteModalContainer";
import ListRenameModalContainer from "app/pages/sales-intelligence/pages/lists/containers/ListRenameModal/ListRenameModalContainer";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import Pagination from "app/pages/sales-intelligence/sub-modules/contacts-search/components/Pagination/Pagination";
import { StyledAutoCreatedTag } from "app/pages/sales-intelligence/sub-modules/opportunities/components/list-items/OpportunityListItem/styles";
import { OpportunityListTypeEnum } from "app/pages/sales-intelligence/sub-modules/opportunities/types";
import signalsAlertsTrackingService from "app/pages/sales-intelligence/sub-modules/signals-alerts/services/tracking/trackingService";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import useStaticListsSignalsAlertsFrequencyMutation from "../../../sub-modules/my-list/mutations/useStaticListsSignalsAlertsFrequencyMutation";

import {
    StyledCustomIcon,
    StyledListHistoryPagination,
    StyledStaticListContainer,
    StyledStaticListItemsContainer,
} from "./styles";

type StaticListContainerProps = {
    records: StaticListDto[];
    totalCount: number;
    setPage: (page: number) => void;
    refetch: () => void;
    page: number;
};

const StaticListContainer: FC<StaticListContainerProps> = ({
    records,
    totalCount,
    setPage,
    refetch,
    page: pageData,
}) => {
    const alertsFrequencyMutation = useStaticListsSignalsAlertsFrequencyMutation();
    const staticListTrackingService = useStaticListTrackingService();
    const trackingService = useTargetAccountsTrackingService();
    const [listBeingRenamed, setListBeingRenamed] = useState<ShortOpportunityList | undefined>(undefined);
    const [listBeingDeleted, setListBeingDeleted] = useState<ShortOpportunityList | undefined>(undefined);

    const canDeleteOpportunities = salesSettingsHelper.canDeleteOpportunities();

    const closeDeleteModal = () => {
        setListBeingDeleted(undefined);
        void refetch();
    };

    const closeRenameModal = () => {
        setListBeingRenamed(undefined);
        void refetch();
    };

    const onChangePageHandler = useCallback(
        async (page: number) => {
            if (page !== pageData) {
                await setPage(page);
            }
        },
        [setPage, pageData],
    );

    const handleMenuItemSelect = (option: MenuItemOption, list: Readonly<ShortOpportunityList>) => {
        switch (option) {
            case "rename":
                return setListBeingRenamed(list);
            case "delete":
                return setListBeingDeleted(list);
            default:
                return;
        }
    };

    const handleTrackedOptionsMenuToggle = (isOpen: boolean): void => {
        staticListTrackingService.trackListOptionsToggled(isOpen);
    };

    const getMenuItem = (list: StaticListDto) => {
        const { listType } = list;

        const isRestrictedList =
            listType === OpportunityListTypeEnum.Shared || listType === OpportunityListTypeEnum.RecentlyViewed;

        const items = [];

        if (!isRestrictedList) {
            items.push(renameMenuItem);
        }

        items.push({ ...deleteMenuItem, disabled: !canDeleteOpportunities });

        return items;
    };

    const handleAlertsToggle = (item: Readonly<StaticListDto>) => {
        const isPaused = item.digestSubscription?.status === "paused";
        const newStatus = isPaused ? "active" : "paused";

        signalsAlertsTrackingService.trackAlertFrequencySelect(newStatus === "active" ? "Weekly" : "Paused");
        alertsFrequencyMutation.mutate({
            assetId: item.id,
            assetType: "opportunityList",
            status: newStatus,
        });
    };

    const handleRedirectToStaticList = (id: string, totalCount: number) => {
        trackingService.trackStaticListClicked(totalCount);
        void proNavigator.go(STATIC_LIST_PAGE_ROUTE, {
            id,
        });
    };

    const handleDeleteSuccess = () => {
        if (records.length === 1 && pageData > 1) {
            setPage(pageData - 1);
        }
    };

    return (
        <StyledStaticListContainer>
            <ListRenameModalContainer
                list={listBeingRenamed}
                onClose={closeRenameModal}
            />
            <ListDeleteModalContainer
                list={listBeingDeleted}
                onClose={closeDeleteModal}
                onDeleteSuccess={handleDeleteSuccess}
            />

            <StyledStaticListItemsContainer>
                {records.map((list: StaticListDto) => {
                    const { id, name, totalCount, lastUpdated, listType, country } = list;

                    const iconConfig: IconConfig = getIconConfigByType(listType);
                    return (
                        <ListItem
                            key={id}
                            name={
                                listType === OpportunityListTypeEnum.RecentlyViewed
                                    ? translate("si.opportunity-list.auto-created.title")
                                    : name
                            }
                            tag={
                                listType === OpportunityListTypeEnum.RecentlyViewed ||
                                listType === OpportunityListTypeEnum.Crm ? (
                                    <StyledAutoCreatedTag />
                                ) : null
                            }
                            icon={
                                <StyledCustomIcon
                                    iconName={
                                        listType === OpportunityListTypeEnum.Shared
                                            ? "assign-to-user-gradient"
                                            : "list-icon-gradient"
                                    }
                                    iconSize={iconConfig.iconSize}
                                    rightSpace={iconConfig.spaceRight}
                                />
                            }
                            onClick={() => handleRedirectToStaticList(id, totalCount)}
                            metadataElements={[
                                <EmailAlertButton
                                    key={`email-alert-button-${id}`}
                                    isPaused={list.digestSubscription?.status === "paused"}
                                    onChange={() => handleAlertsToggle(list)}
                                />,
                                <StyledOptionsMenu
                                    id={`static-list-item-${id}`}
                                    key={`static-list-item-${id}`}
                                    items={getMenuItem(list)}
                                    onSelect={(option) =>
                                        handleMenuItemSelect(option, {
                                            id,
                                            friendlyName: name,
                                            country,
                                            listType,
                                            totalCount,
                                        })
                                    }
                                    disabledTooltipContent={translate(
                                        "si.my-list.static-list.options-menu.disabled.tooltip",
                                    )}
                                    tooltipContent={translate("si.common.option-menu.placeholder")}
                                    onToggle={handleTrackedOptionsMenuToggle}
                                    dropdownWidth={!canDeleteOpportunities ? 320 : 211}
                                    children={!canDeleteOpportunities ? <ListHookOption /> : undefined}
                                    isAppendToBody={false}
                                />,
                            ]}
                            lastUpdated={lastUpdated ? new Date(lastUpdated) : undefined}
                            websitesCount={totalCount}
                        />
                    );
                })}
            </StyledStaticListItemsContainer>
            {totalCount > PAGE_SIZE ? (
                <StyledListHistoryPagination>
                    <Pagination
                        page={pageData}
                        pageSize={PAGE_SIZE}
                        totalCount={totalCount}
                        onPageChange={onChangePageHandler}
                    />
                </StyledListHistoryPagination>
            ) : null}
        </StyledStaticListContainer>
    );
};

export default StaticListContainer;
